import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CircularProgress,
  Chip,
  Stack,
  Alert,
  CardActionArea,
  Pagination,
} from '@mui/material';
import SearchFilters from '../../components/Search/SearchFilters';
import PatternViewImportDialog from '../../components/PatternViewImportDialog';
import patternService from '../../services/pattern.service';
import { ExercisePattern, Skill } from '../../types';

const SearchPatterns: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [requiredSkills, setRequiredSkills] = useState<Skill[]>([]);
  const [desiredSkills, setDesiredSkills] = useState<Skill[]>([]);
  const [forbiddenSkills, setForbiddenSkills] = useState<Skill[]>([]);
  const [patterns, setPatterns] = useState<ExercisePattern[]>([]);
  const [filteredPatterns, setFilteredPatterns] = useState<ExercisePattern[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPattern, setSelectedPattern] = useState<ExercisePattern | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Server-side pagination settings
  const PAGE_SIZE = 20; // Default Django REST framework page size
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Load public patterns with pagination
  useEffect(() => {
    const loadPatterns = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await patternService.getAvailablePatterns(page);
        setPatterns(response.results);
        setTotalCount(response.count);

        // Extract unique tags from current page
        const tags = new Set<string>();
        response.results.forEach((pattern: ExercisePattern) => {
          pattern.tags?.forEach((tag: string) => tags.add(tag));
        });
        setAvailableTags(prev => {
          const allTags = new Set(prev);
          tags.forEach(tag => allTags.add(tag));
          return Array.from(allTags).sort();
        });
      } catch (err) {
        console.error('Error loading patterns:', err);
        setError("Erreur lors du chargement des patterns d'exercices");
      } finally {
        setLoading(false);
      }
    };

    loadPatterns();
  }, [page]);

  // Filter patterns with skill-based filtering and scoring
  useEffect(() => {
    let filtered = [...patterns];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (pattern) =>
          pattern.title?.toLowerCase().includes(query) ||
          pattern.latex_template?.toLowerCase().includes(query)
      );
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter((pattern) =>
        selectedTags.every((tag) => pattern.tags?.includes(tag))
      );
    }

    // Filter by skills
    if (requiredSkills.length > 0 || desiredSkills.length > 0 || forbiddenSkills.length > 0) {
      const requiredSkillIds = new Set(requiredSkills.map(s => s.id));
      const forbiddenSkillIds = new Set(forbiddenSkills.map(s => s.id));
      const desiredSkillIds = new Set(desiredSkills.map(s => s.id));

      const patternsWithScores = filtered.map(pattern => {
        // Get all public solutions for this pattern
        const publicSolutions = pattern.solutions?.filter(sol => sol.is_public) || [];

        if (publicSolutions.length === 0) {
          // No public solutions = pattern is excluded
          return { pattern, score: -1 };
        }

        // Check forbidden skills - if ANY public solution has a forbidden skill, exclude the pattern
        const hasForbiddenSkill = publicSolutions.some(solution =>
          solution.skills?.some(skill => forbiddenSkillIds.has(skill.id))
        );

        if (hasForbiddenSkill) {
          return { pattern, score: -1 };
        }

        // Check required skills - at least ONE public solution must have ALL required skills
        const hasRequiredSkills = publicSolutions.some(solution => {
          const solutionSkillIds = new Set(solution.skills?.map(s => s.id) || []);
          return Array.from(requiredSkillIds).every(reqId => solutionSkillIds.has(reqId));
        });

        if (requiredSkillIds.size > 0 && !hasRequiredSkills) {
          return { pattern, score: -1 };
        }

        // Calculate score based on desired skills across ALL public solutions
        const desiredSkillsCount = publicSolutions.reduce((acc, solution) => {
          const solutionSkillIds = new Set(solution.skills?.map(s => s.id) || []);
          const matchingDesired = Array.from(desiredSkillIds).filter(desId =>
            solutionSkillIds.has(desId)
          );
          return acc + matchingDesired.length;
        }, 0);

        return { pattern, score: desiredSkillsCount };
      });

      // Filter out patterns with score -1 (excluded)
      const validPatterns = patternsWithScores.filter(p => p.score >= 0);

      // Sort by score (highest first)
      validPatterns.sort((a, b) => b.score - a.score);

      filtered = validPatterns.map(p => p.pattern);
    }

    setFilteredPatterns(filtered);
  }, [searchQuery, selectedTags, requiredSkills, desiredSkills, forbiddenSkills, patterns]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Search Filters */}
        <Grid item xs={12} md={3}>
          <SearchFilters
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            availableTags={availableTags}
            contentType="patterns d'exercices"
            requiredSkills={requiredSkills}
            desiredSkills={desiredSkills}
            forbiddenSkills={forbiddenSkills}
            onRequiredSkillsChange={setRequiredSkills}
            onDesiredSkillsChange={setDesiredSkills}
            onForbiddenSkillsChange={setForbiddenSkills}
          />
        </Grid>

        {/* Right Panel - Results */}
        <Grid item xs={12} md={9}>
          <Box>
            {/* Results count */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {totalCount} pattern{totalCount > 1 ? 's' : ''} trouvé{totalCount > 1 ? 's' : ''}
            </Typography>

            {/* Results */}
            <Grid container spacing={2}>
              {filteredPatterns.map((pattern) => (
                <Grid item xs={12} sm={6} md={4} key={pattern.id}>
                  <Card>
                    <CardActionArea onClick={() => {
                      setSelectedPattern(pattern);
                      setDialogOpen(true);
                    }}>
                      {pattern.thumbnail_url && (
                        <CardMedia
                          component="img"
                          height="200"
                          image={pattern.thumbnail_url}
                          alt={pattern.title}
                          sx={{ objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                        />
                      )}
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {pattern.title}
                        </Typography>

                        {pattern.created_by_name && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Par {pattern.created_by_name}
                          </Typography>
                        )}

                        {pattern.exercise_count !== undefined && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {pattern.exercise_count} exercice{pattern.exercise_count > 1 ? 's' : ''}
                          </Typography>
                        )}

                        {pattern.tags && pattern.tags.length > 0 && (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                            {pattern.tags.map((tag) => (
                              <Chip key={tag} label={tag} size="small" variant="outlined" />
                            ))}
                          </Stack>
                        )}
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {totalCount === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="text.secondary">
                  Aucun pattern ne correspond à vos critères de recherche
                </Typography>
              </Box>
            )}

            {/* Bottom Pagination */}
            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <PatternViewImportDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        pattern={selectedPattern}
        onImportSuccess={() => {
          // Optionally show a success message
          console.log('Pattern imported successfully');
        }}
      />
    </Box>
  );
};

export default SearchPatterns;
